/**
 * Configuración → Tipos de documento (`/configuracion/tipos-documento`).
 *
 * Puerto de `Document_types` + `views/document_types/*`: el catálogo de códigos
 * SUNAT (01 Factura, 03 Boleta, 99 Nota de venta, 11 Nota de crédito…) con su
 * nombre y descripción.
 *
 * Diferencias con el panel anterior (anotadas en el código):
 * - Se listan **todos** los tipos, activos e inactivos: antes `get_all()` filtraba
 *   `isActive = 1` y un tipo desactivado desaparecía sin forma de recuperarlo.
 * - El código repetido responde 409 también al **editar** (el alta ya lo validaba,
 *   la edición no).
 * - **No hay borrado** (tampoco lo tenía): las series apuntan al tipo, así que se
 *   activa o desactiva.
 */

import { ArrowLeft, FileText, Pencil, Plus, Power, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { useMutarTiposDocumento, useTiposDocumento } from '../catalogos.api';
import type { TipoDocumento } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

const BASE_BANDERA =
  'inline-flex items-center gap-1.5 rounded-control px-3 py-1.5 text-xs font-semibold transition-colors';
const BANDERA_ACTIVA = 'bg-ink-900 text-white';
const BANDERA_INACTIVA = 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300';

/** Alta/edición de un tipo de documento. */
function TipoDialogo({
  tipo,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  tipo: TipoDocumento | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: { codigo: string; nombre: string; descripcion: string; activo?: boolean }) => void;
  onCerrar: () => void;
}) {
  const [codigo, setCodigo] = useState(tipo?.codigo ?? '');
  const [nombre, setNombre] = useState(tipo?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(tipo?.descripcion ?? '');
  const [activo, setActivo] = useState(tipo?.activo ?? true);

  const valido = codigo.trim().length > 0 && nombre.trim().length > 0 && descripcion.trim().length > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={tipo ? `Editar ${tipo.nombre}` : 'Nuevo tipo de documento'}
      description="El código es el de SUNAT (01, 03, 99…) y no puede repetirse."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                codigo: codigo.trim().toUpperCase(),
                nombre: nombre.trim(),
                descripcion: descripcion.trim(),
                ...(tipo ? { activo } : {}),
              })
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-[7rem_1fr]">
          <Input
            label="Código"
            value={codigo}
            maxLength={2}
            autoFocus={!tipo}
            placeholder="01"
            className="uppercase"
            onChange={(evento) => setCodigo(evento.target.value.toUpperCase())}
          />
          <Input
            label="Nombre"
            value={nombre}
            maxLength={50}
            placeholder="Ej. Factura"
            onChange={(evento) => setNombre(evento.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="tipo-documento-descripcion" className="text-sm font-medium text-ink-700">
            Descripción
          </label>
          <textarea
            id="tipo-documento-descripcion"
            className="min-h-20 w-full rounded-control border border-ink-200 bg-white px-3 py-2 text-sm text-ink-800 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
            value={descripcion}
            placeholder="Describe el tipo de documento…"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>

        {tipo ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-ink-700">Estado</span>
            {[true, false].map((valor) => (
              <button
                key={String(valor)}
                type="button"
                aria-pressed={activo === valor}
                className={`${BASE_BANDERA} ${activo === valor ? BANDERA_ACTIVA : BANDERA_INACTIVA}`}
                onClick={() => setActivo(valor)}
              >
                {valor ? 'Activo' : 'Inactivo'}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

export function TiposDocumentoPage() {
  useDocumentTitle('Configuración · Tipos de documento');

  const tipos = useTiposDocumento();
  const mutaciones = useMutarTiposDocumento();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; tipo: TipoDocumento | null }>({
    abierto: false,
    tipo: null,
  });
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = tipos.error instanceof ApiError && tipos.error.status === 403;
  const lista = tipos.data?.tipos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <FileText className="size-6 text-brand-500" aria-hidden="true" />
          Tipos de documento
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Comprobantes e informes que puede emitir el sistema. Cada tipo puede tener varias series.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, tipo: null });
        }}
      >
        Nuevo tipo
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: {
    codigo: string;
    nombre: string;
    descripcion: string;
    activo?: boolean;
  }) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, tipo: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar el tipo.')),
    };

    if (dialogo.tipo) {
      mutaciones.actualizar.mutate({ id: dialogo.tipo.id, payload }, alGuardar);
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = (tipo: TipoDocumento) => {
    mutaciones.cambiarEstado.mutate(
      { id: tipo.id, activo: !tipo.activo },
      {
        onSuccess: (datos) => setAviso({ tono: 'success', texto: datos.mensaje }),
        onError: (error) =>
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {tipos.isLoading ? (
        <Skeleton className="h-80 w-full" />
      ) : tipos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los tipos de documento">
          {mensajeDe(tipos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Listado de tipos de documento</CardTitle>
            <Badge variant="neutral">{tipos.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[48rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-20 pb-2">Código</th>
                    <th className="w-44 pb-2">Nombre</th>
                    <th className="pb-2">Descripción</th>
                    <th className="w-20 pb-2 text-center">Series</th>
                    <th className="w-24 pb-2">Estado</th>
                    <th className="w-28 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((tipo) => (
                    <tr key={tipo.id} className="border-t border-ink-100 align-top">
                      <td className="py-3">
                        <span className="rounded-control bg-ink-100 px-2 py-0.5 font-mono text-xs font-semibold text-ink-700">
                          {tipo.codigo}
                        </span>
                      </td>
                      <td className="py-3 font-medium text-ink-900">{tipo.nombre}</td>
                      <td className="py-3 text-xs leading-relaxed text-ink-600">
                        {tipo.descripcion}
                      </td>
                      <td className="py-3 text-center text-ink-700">{tipo.series}</td>
                      <td className="py-3">
                        <Badge variant={tipo.activo ? 'success' : 'neutral'}>
                          {tipo.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${tipo.nombre}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, tipo });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={tipo.activo ? `Desactivar ${tipo.nombre}` : `Activar ${tipo.nombre}`}
                            title={tipo.activo ? 'Desactivar' : 'Activar'}
                            loading={mutaciones.cambiarEstado.isPending}
                            onClick={() => cambiarEstado(tipo)}
                          >
                            <Power
                              className={`size-4 ${tipo.activo ? 'text-emerald-600' : 'text-ink-400'}`}
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert variant="info" className="mt-4">
              Los tipos no se eliminan: las series de documento apuntan a ellos. Desactivar un tipo
              oculta sus series sin borrar nada.
            </Alert>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <TipoDialogo
          key={`tipo-${dialogo.tipo?.id ?? 'nuevo'}`}
          tipo={dialogo.tipo}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, tipo: null })}
        />
      ) : null}
    </div>
  );
}
